// Services.js
import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
// Import necessary icons
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SpaIcon from '@mui/icons-material/Spa';
import BrushIcon from '@mui/icons-material/Brush';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import HealingIcon from '@mui/icons-material/Healing';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard'; // Assuming ServiceCard is ready for translated props
import { useAppLanguage } from '../hooks/useAppLanguage'; // NEW: Import the language hook

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useAppLanguage(); // NEW: Initialize the language hook to get the t function

  // NEW: Define services data inside the component to use the 't' function
  // You will need to add these keys to your translation JSON files
  // e.g., public/locales/en/translation.json and public/locales/zh/translation.json
  const servicesData = [
    {
      key: 'general', // Used for generating translation keys
      icon: <LocalHospitalIcon sx={{ fontSize: 40 }} />,
      gradient: 'primary.main',
    },
    {
      key: 'cosmetic',
      icon: <BrushIcon sx={{ fontSize: 40 }} />,
      gradient: 'primary.main',
    },
    {
      key: 'emergency',
      icon: <HealingIcon sx={{ fontSize: 40 }} />,
      gradient: 'primary.main',
    },
    {
      key: 'pediatric',
      icon: <ChildCareIcon sx={{ fontSize: 40 }} />,
      gradient: 'primary.main',
    },
    {
      key: 'implants',
      icon: <SpaIcon sx={{ fontSize: 40 }} />,
      gradient: 'primary.main',
    },
  ];

  // NEW: Map over servicesData to create the translated services array
  const translatedServices = servicesData.map(service => ({
    ...service, // Keep icon and gradient
    display_title: t(`services.${service.key}.display_title`),
    title: t(`services.${service.key}.title`),
    description: t(`services.${service.key}.description`, `Default ${service.key} description.`),
    // Assuming details are also an array of strings that need translation
    // The number of details might vary, so we'll handle them generically.
    // You'll need to ensure your translation files have entries like:
    // "services.general.details.0": "Dental Exams & Cleanings",
    // "services.general.details.1": "Fillings", ...
    // "services.cosmetic.details.0": "Teeth Whitening", ...
    details: (t(`services.${service.key}.details`, { returnObjects: true, defaultValue: [] }) as string[]),
    display_details: (t(`services.${service.key}.display_details`, { returnObjects: true, defaultValue: [] }) as string[]),
  }));


  return (
    <Box 
      id="services" 
      sx={{ 
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'radial-gradient(circle at 50% 50%, rgba(26,35,126,0.05) 0%, rgba(26,35,126,0) 70%)',
        }
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            align="left" // Changed from "center" to "left" as per original selected code.
            gutterBottom
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, md: 0 },
              fontWeight: 700,
              color: 'text.secondary' // Changed from 'primary.contrastText' to 'text.secondary'
            }}
          >
            {/* NEW: Translate the page title */}
            {t('services.pageTitle', 'Our Services')}
          </Typography>
        </motion.div>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* NEW: Use translatedServices array */}
          {translatedServices.map((service, index) => (
            <ServiceCard
              key={service.title} // Title should be unique after translation, or use service.key
              service={service} // Pass the fully translated service object
              index={index}
              isMobile={isMobile}
            />
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
